import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Target, User, TrendingUp, Users, Shield, Calendar, Ruler, Scale, Check, ChevronRight, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";

interface ComprehensiveOnboardingProps {
  open: boolean;
  onComplete: () => void;
  userId: string;
  allowClose?: boolean;
}

const TOTAL_STEPS = 5;

export const ComprehensiveOnboarding = ({ open, onComplete, userId, allowClose = false }: ComprehensiveOnboardingProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    fitness_goal: "",
    date_of_birth: "",
    height: "",
    weight: "",
    gender: "",
    experience_level: "",
    privacy_acknowledged: false,
  });

  const progress = (currentStep / TOTAL_STEPS) * 100;

  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const calculateBMI = (height: number, weight: number) => {
    if (!height || !weight) return null;
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);
    return bmi.toFixed(1);
  };

  const age = calculateAge(formData.date_of_birth);
  const bmi = calculateBMI(parseFloat(formData.height), parseFloat(formData.weight));

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.fitness_goal !== "";
      case 2:
        return formData.date_of_birth && formData.height && formData.weight && formData.gender;
      case 3:
        return formData.experience_level !== "";
      case 4:
        return formData.privacy_acknowledged;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);

    const { error } = await supabase
      .from("profiles")
      .update({
        fitness_goal: formData.fitness_goal,
        experience_level: formData.experience_level,
        date_of_birth: formData.date_of_birth || null,
        height: formData.height ? parseFloat(formData.height) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        gender: formData.gender || null,
        onboarding_completed: true,
      })
      .eq("id", userId);

    setLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save your information. Please try again.",
      });
      return;
    }

    // Celebration confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    toast({
      title: "Welcome to FITLINK! 🎉",
      description: "Your profile is all set up. Let's start your fitness journey!",
    });

    localStorage.setItem("onboarding_completed", "true");
    onComplete();
    navigate("/dashboard");
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Target className="w-8 h-8 text-primary" />
              </div>
            </div>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-2">What's Your Fitness Goal?</h3>
              <p className="text-sm text-muted-foreground">
                This helps us personalize your experience
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { value: "build_muscle", label: "Build Muscle", icon: "💪" },
                { value: "lose_weight", label: "Lose Weight", icon: "🔥" },
                { value: "get_stronger", label: "Get Stronger", icon: "🏋️" },
                { value: "improve_endurance", label: "Improve Endurance", icon: "🏃" },
                { value: "stay_active", label: "Stay Active", icon: "⚡" },
                { value: "athletic_performance", label: "Athletic Performance", icon: "🏆" },
              ].map((goal) => (
                <Button
                  key={goal.value}
                  variant={formData.fitness_goal === goal.value ? "default" : "outline"}
                  className="h-14 justify-start text-left"
                  onClick={() => setFormData({ ...formData, fitness_goal: goal.value })}
                >
                  <span className="text-2xl mr-3">{goal.icon}</span>
                  <span className="font-semibold">{goal.label}</span>
                  {formData.fitness_goal === goal.value && (
                    <Check className="w-5 h-5 ml-auto" />
                  )}
                </Button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
            </div>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-2">Personal Information</h3>
              <p className="text-sm text-muted-foreground">
                Help us provide better recommendations
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dob" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  Date of Birth
                </Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                />
                {age !== null && (
                  <p className="text-xs text-muted-foreground">Age: {age} years old</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="height" className="flex items-center gap-2">
                    <Ruler className="w-4 h-4 text-muted-foreground" />
                    Height (cm)
                  </Label>
                  <Input
                    id="height"
                    type="number"
                    placeholder="175"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    min="50"
                    max="300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weight" className="flex items-center gap-2">
                    <Scale className="w-4 h-4 text-muted-foreground" />
                    Weight (kg)
                  </Label>
                  <Input
                    id="weight"
                    type="number"
                    placeholder="70"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    min="20"
                    max="500"
                  />
                </div>
              </div>

              {bmi && (
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">BMI</span>
                    <span className="text-lg font-bold">{bmi}</span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => setFormData({ ...formData, gender: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
            </div>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-2">Your Experience Level</h3>
              <p className="text-sm text-muted-foreground">
                This helps us suggest appropriate workouts
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { value: "beginner", label: "Beginner", desc: "Just starting out" },
                { value: "intermediate", label: "Intermediate", desc: "1-2 years experience" },
                { value: "advanced", label: "Advanced", desc: "3+ years experience" },
                { value: "expert", label: "Expert", desc: "Professional/Competitive" },
              ].map((level) => (
                <Button
                  key={level.value}
                  variant={formData.experience_level === level.value ? "default" : "outline"}
                  className="h-16 justify-start text-left flex-col items-start"
                  onClick={() => setFormData({ ...formData, experience_level: level.value })}
                >
                  <span className="font-semibold">{level.label}</span>
                  <span className="text-xs text-muted-foreground">{level.desc}</span>
                  {formData.experience_level === level.value && (
                    <Check className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2" />
                  )}
                </Button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="w-8 h-8 text-primary" />
              </div>
            </div>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-2">Privacy & Community</h3>
              <p className="text-sm text-muted-foreground">
                Understanding data visibility
              </p>
            </div>
            <div className="space-y-4 text-sm">
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Friend-Only Visibility
                </h4>
                <p className="text-muted-foreground">
                  Your workouts, exercises, and progress are only visible to friends you accept.
                </p>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  Private Data
                </h4>
                <p className="text-muted-foreground">
                  Personal info (email, age, body metrics) is never shared with other users.
                </p>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" />
                  Community Features
                </h4>
                <p className="text-muted-foreground">
                  Share workouts, like, comment, and stay motivated with friends.
                </p>
              </div>

              <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={formData.privacy_acknowledged}
                  onChange={(e) => setFormData({ ...formData, privacy_acknowledged: e.target.checked })}
                  className="mt-1"
                />
                <label htmlFor="privacy" className="text-sm cursor-pointer">
                  I understand how my data will be used and shared. View our{" "}
                  <a href="/privacy" className="text-primary underline" target="_blank">
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-8 h-8 text-primary" />
              </div>
            </div>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-2">Find Your Friends</h3>
              <p className="text-sm text-muted-foreground">
                Connect with friends to stay motivated together
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="p-6 bg-muted/50 rounded-lg">
                <p className="text-muted-foreground mb-4">
                  You can search for friends and send them requests to connect and share your fitness journey.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    handleComplete();
                  }}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Find Friends Later
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                You can always add friends from the Friends page
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={allowClose ? onComplete : () => {}}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => allowClose ? undefined : e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            Step {currentStep} of {TOTAL_STEPS}
          </DialogTitle>
          <DialogDescription>
            <Progress value={progress} className="mt-2" />
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">{renderStep()}</div>

        <div className="flex justify-between gap-2 pt-4 border-t">
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          {currentStep < TOTAL_STEPS ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className={currentStep === 1 ? "w-full" : "ml-auto"}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={loading || !canProceed()}
              className="ml-auto"
            >
              {loading ? "Saving..." : "Complete Setup"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
